import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { getApiErrorMessage } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '';
const API_FALLBACK_URL = process.env.REACT_APP_BACKEND_FALLBACK_URL || process.env.REACT_APP_API_FALLBACK_URL || '';

const isProductionHost = typeof window !== 'undefined' && window.location?.hostname && !/^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);

const isRetryableError = (err) => {
  if (!err) return false;
  if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') return true;
  if (err.response?.status >= 500) return true;
  if (err.response?.status === 408) return true;
  return false;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [partialToken, setPartialToken] = useState(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const api = useMemo(() => {
    const base = API_URL ? `${API_URL.replace(/\/$/, '')}/api` : '/api';
    const instance = axios.create({
      baseURL: base,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use((config) => {
      const currentToken = tokenRef.current || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      const env = typeof localStorage !== 'undefined' ? (localStorage.getItem('payzen_env_current') || localStorage.getItem('payzen_env') || 'sandbox') : 'sandbox';
      config.headers['X-Environment'] = env;
      return config;
    });

    return instance;
  }, []);

  const logoutRef = useRef(() => {});

  // Sync X-Environment header source so first API call uses this user's env preference.
  // When user is null (e.g. right after reload before initAuth), do NOT overwrite payzen_env_current
  // so that env switch + reload keeps Live and doesn't get overwritten by guest/sandbox.
  useEffect(() => {
    try {
      if (user?.id) {
        const v = localStorage.getItem(`payzen_env_${user.id}`);
        localStorage.setItem('payzen_env_current', v === 'live' ? 'live' : 'sandbox');
      }
    } catch (_) {}
  }, [user?.id]);

  useEffect(() => {
    api.interceptors.response.use(
      (response) => response,
      (error) => {
        const config = error.config;
        if (error.response?.data && 'detail' in error.response.data) {
          const raw = error.response.data.detail;
          const str = getApiErrorMessage(raw, 'Request failed');
          error.response.data._rawDetail = raw;
          error.response.data.detail = typeof str === 'string' ? str : 'Request failed';
        }
        if (!config._fallbackRetried && API_FALLBACK_URL && isRetryableError(error)) {
          config._fallbackRetried = true;
          config.baseURL = `${API_FALLBACK_URL}/api`;
          return api.request(config);
        }
        if (error.response?.status === 401 && !config?.url?.includes?.('/auth/')) {
          logoutRef.current();
        }
        return Promise.reject(error);
      }
    );
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    const SAFETY_MS = 12000; // Never show auth spinner longer than this
    const safetyTimer = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, SAFETY_MS);

    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedPartial = localStorage.getItem('partial_token');
        if (storedToken) {
          try {
            const response = await api.get('/auth/me', { timeout: 8000 });
            if (!cancelled) setUser(response.data);
          } catch (error) {
            if (!cancelled) {
              localStorage.removeItem('token');
              setToken(null);
              if (storedPartial) {
                setRequires2FA(true);
                setPartialToken(storedPartial);
              }
            }
          }
        } else if (storedPartial) {
          setRequires2FA(true);
          setPartialToken(storedPartial);
        }
      } finally {
        if (!cancelled) setLoading(false);
        clearTimeout(safetyTimer);
      }
    };
    initAuth();
    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
    };
  }, [api]);

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    return response.data;
  };

  const verifyEmail = async (email, code) => {
    const response = await api.post('/auth/verify-email', { email, code });
    return response.data;
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      // 202 = 2FA required (Axios treats 2xx as success, so we must handle it here)
      if (response.status === 202) {
        const detail = response.data?.detail || response.data;
        const partial = detail?.partial_token ?? detail?.partialToken;
        setRequires2FA(true);
        setPartialToken(partial);
        if (partial) try { localStorage.setItem('partial_token', partial); } catch (_) {}
        return { success: false, requires2FA: true, partialToken: partial };
      }
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      try { localStorage.setItem('payzen_last_login', new Date().toISOString()); } catch (_) {}
      setToken(access_token);
      setUser(userData);
      setRequires2FA(false);
        setPartialToken(null);
      return { success: true, user: userData };
    } catch (error) {
      if (error.response?.status === 202) {
        const detail = error.response?.data?.detail || error.response?.data;
        const partial = detail?.partial_token ?? detail?.partialToken;
        setRequires2FA(true);
        setPartialToken(partial);
        if (partial) try { localStorage.setItem('partial_token', partial); } catch (_) {}
        return { success: false, requires2FA: true, partialToken: partial };
      }
      throw error;
    }
  };

  const verify2FA = async (code) => {
    const tokenToUse = partialToken || localStorage.getItem('partial_token');
    if (!tokenToUse) {
      throw new Error('Session expired. Please sign in again.');
    }
    const response = await api.post('/auth/verify-2fa', { code: String(code).trim() }, {
      headers: { Authorization: `Bearer ${tokenToUse}` },
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    try { localStorage.setItem('payzen_last_login', new Date().toISOString()); } catch (_) {}
    setToken(access_token);
    setUser(userData);
    setRequires2FA(false);
    setPartialToken(null);
    try { localStorage.removeItem('partial_token'); } catch (_) {}
    return { ...response.data, user: userData };
  };

  const setup2FA = async () => {
    const response = await api.post('/auth/setup-2fa');
    return response.data;
  };

  const enable2FA = async (code) => {
    const response = await api.post('/auth/enable-2fa', { code });
    setUser((prev) => ({ ...prev, is_2fa_enabled: true }));
    return response.data;
  };

  const disable2FA = async (code) => {
    const response = await api.post('/auth/disable-2fa', { code });
    setUser((prev) => ({ ...prev, is_2fa_enabled: false }));
    return response.data;
  };

  const logout = () => {
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && (k.startsWith('payzen_2fa_dismissed') || k.startsWith('payzen_kyc_approved_seen'))) sessionStorage.removeItem(k);
      }
    } catch (_) {}
    localStorage.removeItem('token');
    try { localStorage.removeItem('partial_token'); } catch (_) {}
    setToken(null);
    setUser(null);
    setRequires2FA(false);
    setPartialToken(null);
  };
  logoutRef.current = logout;

  const updateProfile = async (name) => {
    const response = await api.put('/settings/profile', { name });
    setUser((prev) => ({ ...prev, name }));
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await api.put('/settings/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch {
      // ignore
    }
  };

  const value = {
    user,
    token,
    loading,
    requires2FA,
    partialToken,
    api,
    backendMisconfigured: isProductionHost && !API_URL,
    register,
    verifyEmail,
    login,
    verify2FA,
    setup2FA,
    enable2FA,
    disable2FA,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
